<script setup lang="ts">
/**
 * Info Tooltip
 * Hover-triggered tooltip with auto-positioning and arrow.
 *
 * Usage:
 *   <InfoTooltip content="Helpful text" />
 *   <InfoTooltip position="bottom"><template #content>Rich HTML</template></InfoTooltip>
 */
import { ref, onMounted, onUnmounted, nextTick } from 'vue'
import { Info } from 'lucide-vue-next'

type Position = 'top' | 'bottom' | 'left' | 'right' | 'bottom-right' | 'bottom-left'

const props = withDefaults(defineProps<{
  content?: string
  position?: Position
  delay?: number
  iconSize?: number
}>(), {
  content: '',
  position: 'top',
  delay: 100,
  iconSize: 14,
})

const triggerRef = ref<HTMLElement | null>(null)
const tooltipRef = ref<HTMLElement | null>(null)
const arrowRef = ref<HTMLElement | null>(null)
const visible = ref(false)
const currentPos = ref<Position>(props.position)
let showTimer: number | undefined
let hideTimer: number | undefined

function show() {
  clearTimeout(hideTimer)
  showTimer = window.setTimeout(async () => {
    visible.value = true
    await nextTick()
    positionTooltip()
  }, props.delay)
}

function hide() {
  clearTimeout(showTimer)
  hideTimer = window.setTimeout(() => {
    visible.value = false
  }, 100)
}

function getPosition(): Position {
  if (props.position !== 'top') return props.position

  if (triggerRef.value) {
    const rect = triggerRef.value.getBoundingClientRect()
    if (rect.top < 100 && (window.innerHeight - rect.bottom) > rect.top) {
      return 'bottom'
    }
  }
  return 'top'
}

function positionTooltip() {
  const trigger = triggerRef.value
  const tooltip = tooltipRef.value
  const arrow = arrowRef.value
  if (!trigger || !tooltip || !arrow) return

  const pos = getPosition()
  currentPos.value = pos
  const rect = trigger.getBoundingClientRect()
  const tooltipRect = tooltip.getBoundingClientRect()
  const padding = 10

  let top = 0
  let left = 0
  let arrowLeft = 0
  let arrowTop = 0

  switch (pos) {
    case 'bottom':
      top = rect.bottom + 10
      left = rect.left + rect.width / 2 - tooltipRect.width / 2
      arrowLeft = tooltipRect.width / 2 - 6
      arrowTop = -6
      break
    case 'bottom-right':
      top = rect.bottom + 10
      left = rect.right - tooltipRect.width + 10
      arrowLeft = tooltipRect.width - 20
      arrowTop = -6
      break
    case 'bottom-left':
      top = rect.bottom + 10
      left = rect.left - 10
      arrowLeft = 14
      arrowTop = -6
      break
    case 'left':
      top = rect.top + rect.height / 2 - tooltipRect.height / 2
      left = rect.left - tooltipRect.width - 10
      arrowLeft = tooltipRect.width - 6
      arrowTop = tooltipRect.height / 2 - 6
      break
    case 'right':
      top = rect.top + rect.height / 2 - tooltipRect.height / 2
      left = rect.right + 10
      arrowLeft = -6
      arrowTop = tooltipRect.height / 2 - 6
      break
    case 'top':
    default:
      top = rect.top - tooltipRect.height - 10
      left = rect.left + rect.width / 2 - tooltipRect.width / 2
      arrowLeft = tooltipRect.width / 2 - 6
      arrowTop = tooltipRect.height - 6
      break
  }

  if (left < padding) {
    const diff = padding - left
    left = padding
    arrowLeft -= diff
  }
  if (left + tooltipRect.width > window.innerWidth - padding) {
    const diff = left + tooltipRect.width - (window.innerWidth - padding)
    left -= diff
    arrowLeft += diff
  }

  tooltip.style.top = `${top}px`
  tooltip.style.left = `${left}px`
  arrow.style.left = `${arrowLeft}px`

  if (pos === 'left' || pos === 'right') {
    arrow.style.top = `${arrowTop}px`
    arrow.style.left = pos === 'left' ? `${tooltipRect.width - 6}px` : '-6px'
  } else {
    arrow.style.top = pos.startsWith('bottom') ? '-6px' : `${tooltipRect.height - 6}px`
  }
}

function onScroll() {
  if (visible.value) hide()
}

onMounted(() => {
  document.addEventListener('scroll', onScroll, true)
})

onUnmounted(() => {
  clearTimeout(showTimer)
  clearTimeout(hideTimer)
  document.removeEventListener('scroll', onScroll, true)
})
</script>

<template>
  <span
    ref="triggerRef"
    class="inline-flex items-center cursor-help ml-1 text-foreground-muted align-middle transition-colors duration-150 hover:text-brand"
    @mouseenter="show"
    @mouseleave="hide"
  >
    <slot>
      <Info :size="iconSize" />
    </slot>
  </span>

  <Teleport to="body">
    <div
      v-show="visible"
      ref="tooltipRef"
      :class="[
        'tooltip-popup',
        visible && 'tooltip-visible',
      ]"
    >
      <div ref="arrowRef" class="tooltip-arrow" />
      <div class="relative z-[1]">
        <slot name="content">{{ content }}</slot>
      </div>
    </div>
  </Teleport>
</template>

<style scoped>
/* Minimal scoped CSS for JS-driven fixed positioning (no Tailwind equivalent) */
.tooltip-popup {
  position: fixed;
  z-index: 10001;
  width: 220px;
  max-width: 280px;
  padding: 10px 12px;
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: 8px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
  font-size: 12px;
  font-weight: 400;
  line-height: 1.5;
  color: var(--color-foreground-secondary);
  text-align: left;
  pointer-events: none;
  opacity: 0;
  visibility: hidden;
  transform: translateY(4px);
  transition: opacity 0.15s, visibility 0.15s, transform 0.15s;
}

.tooltip-visible {
  opacity: 1;
  visibility: visible;
  transform: translateY(0);
}

.tooltip-arrow {
  position: absolute;
  width: 12px;
  height: 12px;
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  transform: rotate(45deg);
}
</style>
